import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Chip,
  InputAdornment,
  CircularProgress,
  Pagination,
  Dialog,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import HeaderApp from "../../components/HeaderApp";
import CreateCourseForm from "../../components/CreateCourseForm";
import EditCourseForm from "../../components/EditCourseForm";
import AddCourseQuestionsForm from "../../components/AddCourseQuestionsForm";
import { getCursos } from "../../services/cursosService";
import { useResolvableImageSrc } from "../../hooks/useResolvableImageSrc";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const CourseCover = ({ raw, alt }) => {
  const src = useResolvableImageSrc(raw);
  if (!src) {
    return <Typography variant="body2" color="text.secondary">Sin imagen</Typography>;
  }
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
    />
  );
};

const Course = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const PAGE_SIZE = 6;
  const [view, setView] = useState("landing");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [cursos, setCursos] = useState([]);
  const [loadingCursos, setLoadingCursos] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [questionsModalOpen, setQuestionsModalOpen] = useState(false);

  const fetchCursos = async () => {
    try {
      setLoadingCursos(true);
      const response = await getCursos();
      const list = Array.isArray(response) ? response : response?.data ?? [];
      setCursos(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Error al cargar cursos:", error);
      setCursos([]);
    } finally {
      setLoadingCursos(false);
    }
  };

  useEffect(() => {
    fetchCursos();
  }, []);

  const handleBackToLanding = () => {
    setView("landing");
    setSelectedCourse(null);
    setQuestionsModalOpen(false);
    fetchCursos();
  };

  const handleCloseQuestionsModal = () => {
    setQuestionsModalOpen(false);
    setSelectedCourse(null);
  };

  const normalizarCurso = (curso) => ({
    id: curso?.idCurso ?? curso?.id ?? curso?.IdCurso,
    nombre: curso?.Nombre ?? curso?.nombre ?? "Sin nombre",
    descripcion: curso?.Descripcion ?? curso?.descripcion ?? "",
    imagen: curso?.ImagenPortada ?? curso?.imagenPortada ?? "",
    estado: String(curso?.Estado ?? curso?.estado ?? "1"),
  });

  const cursosFiltrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cursos;
    return cursos.filter((cursoRaw) => {
      const curso = normalizarCurso(cursoRaw);
      return (
        String(curso.id ?? "").toLowerCase().includes(q) ||
        curso.nombre.toLowerCase().includes(q) ||
        curso.descripcion.toLowerCase().includes(q)
      );
    });
  }, [cursos, search]);

  const totalPages = Math.max(1, Math.ceil(cursosFiltrados.length / PAGE_SIZE));

  const cursosPaginados = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return cursosFiltrados.slice(start, start + PAGE_SIZE);
  }, [cursosFiltrados, page, PAGE_SIZE]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  if (String(user?.idRol) !== "2") {
    return <Navigate to="/AvailableCourses" replace />;
  }

  return (
    <>
      <HeaderApp />
      <Box
        component="main"
        sx={{
          flex: 1,
          py: 4,
          px: 2,
          bgcolor: "background.default",
          minHeight: "calc(100vh - 64px)",
        }}
      >
        <Container maxWidth={view === "landing" ? "lg" : false}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Cursos
          </Typography>

          {view === "landing" && (
            <>
              <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                <TextField
                  size="small"
                  placeholder="Buscar cursos por ID, nombre o descripción"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ minWidth: 320, flex: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button variant="contained" onClick={() => setView("create")}>
                  Crear curso
                </Button>
              </Box>

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
                mb={2}
              >
                <Typography variant="body2" color="text.secondary">
                  {cursosFiltrados.length} curso{cursosFiltrados.length === 1 ? "" : "s"} encontrado{cursosFiltrados.length === 1 ? "" : "s"}
                </Typography>
                {cursosFiltrados.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Página {page} de {totalPages}
                  </Typography>
                )}
              </Box>

              {loadingCursos ? (
                <Box py={6} display="flex" justifyContent="center">
                  <CircularProgress />
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {cursosPaginados.map((cursoRaw) => {
                    const curso = normalizarCurso(cursoRaw);
                    return (
                      <Grid item xs={12} sm={6} md={4} key={`curso-${curso.id}`}>
                        <Card
                          sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            transition: "transform .2s ease, box-shadow .2s ease",
                            "&:hover": {
                              transform: "translateY(-3px)",
                              boxShadow: 3,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              height: 160,
                              backgroundColor: "#f5f5f5",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                          >
                            <CourseCover raw={curso.imagen} alt={curso.nombre} />
                          </Box>
                          <CardContent sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {curso.nombre}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {curso.id}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mt: 1,
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {curso.descripcion || "Sin descripción"}
                            </Typography>
                            <Box mt={1}>
                              <Chip
                                size="small"
                                label={curso.estado === "1" ? "Activo" : "Inactivo"}
                                variant="outlined"
                                sx={
                                  curso.estado === "1"
                                    ? {
                                        color: "#1B5E20",
                                        borderColor: "#2E7D32",
                                        backgroundColor: "#E8F5E9",
                                        fontWeight: 600,
                                      }
                                    : {
                                        color: "#616161",
                                        borderColor: "#9E9E9E",
                                        backgroundColor: "#F5F5F5",
                                        fontWeight: 500,
                                      }
                                }
                              />
                            </Box>
                          </CardContent>
                          <CardActions sx={{ px: 2, pb: 2 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{
                                color: "#455A64",
                                borderColor: "#90A4AE",
                                backgroundColor: "#F5F7F8",
                                "&:hover": {
                                  borderColor: "#607D8B",
                                  backgroundColor: "#ECEFF1",
                                },
                              }}
                              onClick={() => {
                                setSelectedCourse(curso);
                                setView("edit");
                              }}
                            >
                              Editar
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => {
                                setSelectedCourse(curso);
                                setQuestionsModalOpen(true);
                              }}
                            >
                              Agregar preguntas
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    );
                  })}
                  {!loadingCursos && cursosFiltrados.length === 0 && (
                    <Grid item xs={12}>
                      <Box py={7} textAlign="center" sx={{ border: "1px dashed #ddd", borderRadius: 2 }}>
                        <Typography color="text.secondary">
                          No se encontraron cursos con ese criterio.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Prueba buscando por otro nombre o por ID.
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              )}

              {!loadingCursos && cursosFiltrados.length > PAGE_SIZE && (
                <Box mt={3} display="flex" justifyContent="center">
                  <Pagination
                    page={page}
                    count={totalPages}
                    shape="rounded"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        color: "#616161",
                        borderColor: "#BDBDBD",
                      },
                      "& .Mui-selected": {
                        backgroundColor: "#E0E0E0 !important",
                        color: "#424242",
                        borderColor: "#9E9E9E",
                        fontWeight: 600,
                      },
                    }}
                    onChange={(_, newPage) => {
                      setPage(newPage);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  />
                </Box>
              )}
            </>
          )}

          {view === "create" && (
            <>
              <Box mb={2}>
                <Button variant="text" onClick={handleBackToLanding}>
                  Volver al listado
                </Button>
              </Box>
              <CreateCourseForm onSuccess={handleBackToLanding} />
            </>
          )}

          {view === "edit" && selectedCourse && (
            <>
              <Box mb={2}>
                <Button variant="text" onClick={handleBackToLanding}>
                  Volver al listado
                </Button>
              </Box>
              <EditCourseForm
                initialCourseId={selectedCourse.id}
                onSuccess={handleBackToLanding}
              />
            </>
          )}

        </Container>
      </Box>

      <Dialog
        open={questionsModalOpen && !!selectedCourse}
        onClose={handleCloseQuestionsModal}
        fullWidth
        fullScreen={isMobile}
        maxWidth="lg"
      >
        <Box display="flex" justifyContent="flex-end" px={1} pt={1}>
          <IconButton aria-label="Cerrar modal" onClick={handleCloseQuestionsModal}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ pt: 0, px: 0 }}>
          {selectedCourse && (
            <AddCourseQuestionsForm
              idCurso={selectedCourse.id}
              nombreCurso={selectedCourse.nombre}
              onSuccess={handleBackToLanding}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Course;
